/**
 * MovePaymentsPage — payments sub-page for /moves/:quoteId/payments
 *
 * Shows payment summary, schedule, lets the user log manual payments,
 * send payment requests, and view payment history.
 */
import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  Send,
  DollarSign,
  XCircle,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { useSharedQuote } from '../../context/QuoteContext';
import { useUserProfile } from '../../context/UserProfileContext';
import { useManualPayments, OfflinePaymentMethodType } from '../../hooks/useManualPayments';
import { usePaymentRequest } from '../../hooks/usePaymentRequest';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { BottomSheet } from './MovePageComponents';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const METHOD_LABELS: Record<string, string> = {
  venmo: 'Venmo',
  cashapp: 'Cash App',
  paypal: 'PayPal',
  zelle: 'Zelle',
  check: 'Check',
  cash: 'Cash',
  custom: 'Custom',
  stripe: 'Stripe',
};

const INSTALLMENT_STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-stone/50', text: 'text-slate', label: 'Pending' },
  due: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Due' },
  paid: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Paid' },
  overdue: { bg: 'bg-red-50', text: 'text-red-700', label: 'Overdue' },
  waived: { bg: 'bg-stone/30', text: 'text-slate', label: 'Waived' },
};

const PAYMENT_STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  no_schedule: { color: 'text-slate', label: 'No Schedule' },
  unpaid: { color: 'text-red-600', label: 'Unpaid' },
  partially_paid: { color: 'text-amber-600', label: 'Partially Paid' },
  fully_paid: { color: 'text-emerald-600', label: 'Fully Paid' },
  overpaid: { color: 'text-blue-600', label: 'Overpaid' },
};

const PAYMENT_METHODS: { value: OfflinePaymentMethodType; label: string }[] = [
  { value: 'venmo', label: 'Venmo' },
  { value: 'cashapp', label: 'Cash App' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'check', label: 'Check' },
  { value: 'cash', label: 'Cash' },
  { value: 'custom', label: 'Custom' },
];

// ── Page ─────────────────────────────────────────────────────────────────────

export function MovePaymentsPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading: quoteLoading, refetch: refetchQuote } = useSharedQuote();

  const {
    payments,
    loading: paymentsLoading,
    logging,
    logPayment,
    voidPayment,
  } = useManualPayments(orgId, quoteId ?? null);

  const {
    history: requestHistory,
    loading: requestsLoading,
    sending,
    sendRequest,
  } = usePaymentRequest(orgId, quoteId ?? null);

  const [showLogSheet, setShowLogSheet] = useState(false);
  const [showRequestSheet, setShowRequestSheet] = useState(false);
  const [voidingId, setVoidingId] = useState<string | null>(null);

  // Payment data lives on the Firestore document but isn't in the web Quote type yet
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawQuote = quote as any;
  // Backend stores the payment summary as 'paymentSummaryCache'
  const summaryCache = rawQuote?.paymentSummaryCache as {
    paymentStatus?: string;
    subtotalAmountCents?: number;
    totalAmountCents: number;
    collectedAmountCents: number;
    outstandingAmountCents: number;
    taxState?: string;
    laborTaxRate?: number;
    taxAmountCents?: number;
    requiresManualTaxReview?: boolean;
    nextPaymentDue?: {
      installmentId: string;
      label: string;
      amountCents: number;
      dueDate?: string;
    };
  } | undefined;

  // Fallback: if no payment summary cache exists, derive a basic summary from formula data
  const summary = summaryCache ?? (quote?.formula ? (() => {
    const subtotalCents = Math.round((quote.formula.finalCostEstimate ?? 0) * 100);
    const taxCents = Math.round((quote.taxAmount ?? 0) * 100);
    const totalCents = subtotalCents + taxCents;
    const collectedCents = payments.reduce((sum, p) => sum + p.amountCents, 0);
    return {
      paymentStatus: collectedCents >= totalCents && totalCents > 0 ? 'fully_paid'
        : collectedCents > 0 ? 'partially_paid'
        : totalCents > 0 ? 'unpaid' : 'no_schedule',
      subtotalAmountCents: subtotalCents,
      totalAmountCents: totalCents,
      collectedAmountCents: collectedCents,
      outstandingAmountCents: totalCents - collectedCents,
      taxState: quote.taxState,
      laborTaxRate: quote.laborTaxRate,
      taxAmountCents: taxCents,
      requiresManualTaxReview: quote.requiresManualTaxReview,
      nextPaymentDue: undefined,
    };
  })() : undefined);
  const schedule = rawQuote?.paymentSchedule as {
    type?: string;
    installments: Array<{
      id: string;
      label: string;
      type: string;
      amountCents: number;
      percentage?: number;
      dueDate?: string;
      status: string;
    }>;
  } | undefined;
  const isLoading = quoteLoading || paymentsLoading || requestsLoading;

  const handleLogPayment = useCallback(
    async (data: {
      method: OfflinePaymentMethodType;
      amountCents: number;
      installmentId?: string;
      referenceNumber?: string;
      note?: string;
    }) => {
      await logPayment(data);
      setShowLogSheet(false);
      refetchQuote();
    },
    [logPayment, refetchQuote],
  );

  const handleVoid = useCallback(
    async (paymentId: string) => {
      setVoidingId(paymentId);
      try {
        await voidPayment(paymentId);
        refetchQuote();
      } finally {
        setVoidingId(null);
      }
    },
    [voidPayment, refetchQuote],
  );

  const handleSendRequest = useCallback(
    async (channels: ('sms' | 'email')[]) => {
      await sendRequest(channels);
      setShowRequestSheet(false);
    },
    [sendRequest],
  );

  if (isLoading && !quote) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <AlertCircle size={32} className="text-rose/50 mb-2" />
        <p className="text-small text-rose font-semibold">Record not found</p>
        <button type="button" onClick={() => navigate(-1)}
          className="text-[13px] text-plum hover:underline mt-2">Go Back</button>
      </div>
    );
  }

  const statusConfig = PAYMENT_STATUS_CONFIG[summary?.paymentStatus ?? 'no_schedule']
    ?? PAYMENT_STATUS_CONFIG.no_schedule;

  return (
    <div className="px-4 pt-4 pb-8">
      {/* Mobile back button */}
      <button type="button" onClick={() => navigate(-1)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1">
        <ArrowLeft size={16} />
        Back
      </button>

      <h1 className="font-display text-h3 text-midnight mb-4">Payments</h1>

      <div className="space-y-4">
        {/* ── Action Buttons ──────────────────────────────────────── */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowLogSheet(true)}
            className="flex-1 btn-base bg-plum text-white hover:bg-plum/90 flex items-center justify-center gap-2 min-h-[44px]"
          >
            <DollarSign size={16} />
            Log Payment
          </button>
          <button
            type="button"
            onClick={() => setShowRequestSheet(true)}
            className="flex-1 btn-base bg-white text-midnight border border-stone hover:bg-mist flex items-center justify-center gap-2 min-h-[44px]"
          >
            <Send size={16} />
            Request Payment
          </button>
        </div>

        {/* ── Payment Summary Card ─────────────────────────────────── */}
        <div className="bg-white border border-stone rounded-xl shadow-brand-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate">
              Payment Summary
            </h2>
            <span className={`text-[11px] font-bold uppercase ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </div>

          {summary ? (
            <div className="space-y-2">
              {/* Subtotal + Tax */}
              {summary.subtotalAmountCents != null && (
                <>
                  <div className="flex justify-between text-small">
                    <span className="text-slate">Subtotal</span>
                    <span className="text-midnight">{formatCents(summary.subtotalAmountCents)}</span>
                  </div>
                  <div className="flex justify-between text-small">
                    <span className="text-slate">
                      Tax{summary.taxState ? ` (${summary.taxState})` : ''}
                    </span>
                    {summary.requiresManualTaxReview ? (
                      <span className="text-amber-600 font-medium">TBD</span>
                    ) : (
                      <span className="text-midnight">{formatCents(summary.taxAmountCents ?? 0)}</span>
                    )}
                  </div>
                </>
              )}

              <div className="flex justify-between text-small">
                <span className="text-slate font-semibold">Total</span>
                <span className="text-midnight font-semibold">{formatCents(summary.totalAmountCents)}</span>
              </div>
              <div className="flex justify-between text-small">
                <span className="text-slate">Collected</span>
                <span className="text-emerald-600 font-medium">
                  {formatCents(summary.collectedAmountCents)}
                </span>
              </div>
              <div className="flex justify-between text-small border-t border-stone pt-2">
                <span className="text-midnight font-semibold">Outstanding</span>
                <span className={`font-semibold ${
                  summary.outstandingAmountCents > 0 ? 'text-midnight' : 'text-emerald-600'
                }`}>
                  {formatCents(summary.outstandingAmountCents)}
                </span>
              </div>

              {/* Next payment due */}
              {summary.nextPaymentDue && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
                  <Clock size={14} className="text-amber-600 shrink-0" />
                  <div className="min-w-0">
                    <span className="text-[12px] text-amber-800 font-medium block truncate">
                      Next: {summary.nextPaymentDue.label} — {formatCents(summary.nextPaymentDue.amountCents)}
                    </span>
                    {summary.nextPaymentDue.dueDate && (
                      <span className="text-[11px] text-amber-600">
                        Due {formatDate(summary.nextPaymentDue.dueDate)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-small text-slate">No payment information available.</p>
          )}
        </div>

        {/* ── Payment Schedule ─────────────────────────────────────── */}
        {schedule && schedule.installments.length > 0 && (
          <div className="bg-white border border-stone rounded-xl shadow-brand-sm p-4">
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
              Payment Schedule
            </h2>
            <div className="space-y-2">
              {schedule.installments.map((inst) => {
                const style = INSTALLMENT_STATUS_STYLES[inst.status] ?? INSTALLMENT_STATUS_STYLES.pending;
                return (
                  <div
                    key={inst.id}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg border border-stone/60 ${style.bg}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {inst.status === 'paid' ? (
                        <CheckCircle size={16} className="text-emerald-600 shrink-0" />
                      ) : inst.status === 'overdue' ? (
                        <AlertCircle size={16} className="text-red-500 shrink-0" />
                      ) : inst.status === 'waived' ? (
                        <XCircle size={16} className="text-slate shrink-0" />
                      ) : (
                        <Clock size={16} className="text-slate shrink-0" />
                      )}
                      <div className="min-w-0">
                        <span className={`text-small block truncate ${style.text} ${inst.status === 'waived' ? 'line-through' : ''}`}>
                          {inst.label}
                        </span>
                        {inst.dueDate && (
                          <span className="text-[11px] text-slate block">
                            {formatDate(inst.dueDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-small font-semibold ${style.text} ${inst.status === 'waived' ? 'line-through' : ''}`}>
                        {formatCents(inst.amountCents)}
                      </span>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}>
                        {style.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Manual Payments History ─────────────────────────────── */}
        <div className="bg-white border border-stone rounded-xl shadow-brand-sm p-4">
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
            Payment History
          </h2>
          {payments.length === 0 ? (
            <p className="text-small text-slate py-2">No payments recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {payments.map((p) => (
                <div key={p.id} className="flex items-start justify-between border border-stone/60 rounded-lg px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-small font-semibold text-midnight">
                        {formatCents(p.amountCents)}
                      </span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-stone/50 text-slate font-medium">
                        {METHOD_LABELS[p.method] ?? p.method}
                      </span>
                    </div>
                    {p.referenceNumber && (
                      <span className="text-[11px] text-slate block">Ref: {p.referenceNumber}</span>
                    )}
                    {p.note && (
                      <span className="text-[11px] text-slate block truncate">{p.note}</span>
                    )}
                    <span className="text-[11px] text-slate block">
                      {formatDateTime(p.createdAt)}
                      {p.loggedByName && ` · ${p.loggedByName}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleVoid(p.id)}
                    disabled={voidingId === p.id}
                    className="text-slate hover:text-red-500 transition-colors p-1 shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
                    title="Void payment"
                  >
                    {voidingId === p.id ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Payment Requests History ────────────────────────────── */}
        {requestHistory.length > 0 && (
          <div className="bg-white border border-stone rounded-xl shadow-brand-sm p-4">
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
              Payment Requests Sent
            </h2>
            <div className="space-y-2">
              {requestHistory.map((r) => (
                <div key={r.id} className="border border-stone/60 rounded-lg px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-small font-semibold text-midnight">
                      {formatCents(r.amountCents)}
                    </span>
                    <span className="text-[11px] text-slate">
                      {formatDateTime(r.sentAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-[11px] text-slate">
                      via {r.channels.join(' & ')}
                    </span>
                    {r.sentByName && (
                      <span className="text-[11px] text-slate">· by {r.sentByName}</span>
                    )}
                  </div>
                  {r.stripePaymentUrl && (
                    <a
                      href={r.stripePaymentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-plum hover:underline flex items-center gap-1 mt-1"
                    >
                      Payment link <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Log Payment Sheet ─────────────────────────────────────── */}
      <BottomSheet open={showLogSheet} onClose={() => setShowLogSheet(false)} title="Log Payment">
        <LogPaymentForm
          installments={schedule?.installments}
          logging={logging}
          onSubmit={handleLogPayment}
          onCancel={() => setShowLogSheet(false)}
        />
      </BottomSheet>

      {/* ── Request Payment Sheet ─────────────────────────────────── */}
      <BottomSheet open={showRequestSheet} onClose={() => setShowRequestSheet(false)} title="Send Payment Request">
        <RequestPaymentForm
          sending={sending}
          hasPhone={!!quote.pointOfContact?.phone}
          hasEmail={!!quote.pointOfContact?.email}
          onSubmit={handleSendRequest}
          onCancel={() => setShowRequestSheet(false)}
        />
      </BottomSheet>
    </div>
  );
}

// ── Log Payment Form ─────────────────────────────────────────────────────────

interface LogPaymentFormProps {
  installments?: Array<{ id: string; label: string; status: string; amountCents: number }>;
  logging: boolean;
  onSubmit: (data: {
    method: OfflinePaymentMethodType;
    amountCents: number;
    installmentId?: string;
    referenceNumber?: string;
    note?: string;
  }) => void;
  onCancel: () => void;
}

function LogPaymentForm({ installments, logging, onSubmit, onCancel }: LogPaymentFormProps) {
  const [method, setMethod] = useState<OfflinePaymentMethodType>('cash');
  const [amountStr, setAmountStr] = useState('');
  const [installmentId, setInstallmentId] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const unpaidInstallments = installments?.filter(i => i.status !== 'paid' && i.status !== 'waived') ?? [];

  const handleSubmit = () => {
    const dollars = parseFloat(amountStr);
    if (!dollars || dollars <= 0) {
      setError('Enter a valid amount');
      return;
    }
    setError('');
    onSubmit({
      method,
      amountCents: Math.round(dollars * 100),
      installmentId: installmentId || undefined,
      referenceNumber: referenceNumber || undefined,
      note: note || undefined,
    });
  };

  return (
    <div className="p-4 space-y-4">
      {/* Method */}
      <div>
        <label className="text-[11px] font-bold uppercase tracking-widest text-slate block mb-1">
          Payment Method
        </label>
        <div className="grid grid-cols-3 gap-2">
          {PAYMENT_METHODS.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMethod(m.value)}
              className={`px-3 py-2.5 rounded-lg border text-[13px] font-medium transition-colors min-h-[44px]
                ${method === m.value
                  ? 'border-plum bg-plum/5 text-plum'
                  : 'border-stone bg-white text-midnight hover:bg-mist'}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Amount */}
      <div>
        <label className="text-[11px] font-bold uppercase tracking-widest text-slate block mb-1">
          Amount
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate text-small">$</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="0.00"
            className="w-full border border-stone rounded-lg pl-7 pr-3 py-3 text-body text-midnight
                       focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum min-h-[44px]"
          />
        </div>
      </div>

      {/* Apply to installment */}
      {unpaidInstallments.length > 0 && (
        <div>
          <label className="text-[11px] font-bold uppercase tracking-widest text-slate block mb-1">
            Apply to Installment
          </label>
          <select
            value={installmentId}
            onChange={(e) => setInstallmentId(e.target.value)}
            className="w-full border border-stone rounded-lg px-3 py-3 text-body text-midnight
                       focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum min-h-[44px]"
          >
            <option value="">None (general payment)</option>
            {unpaidInstallments.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.label} — {formatCents(inst.amountCents)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Reference number */}
      <div>
        <label className="text-[11px] font-bold uppercase tracking-widest text-slate block mb-1">
          Reference Number <span className="text-slate font-normal normal-case">(optional)</span>
        </label>
        <input
          type="text"
          value={referenceNumber}
          onChange={(e) => setReferenceNumber(e.target.value)}
          placeholder="e.g. transaction ID"
          className="w-full border border-stone rounded-lg px-3 py-3 text-body text-midnight
                     focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum min-h-[44px]"
        />
      </div>

      {/* Note */}
      <div>
        <label className="text-[11px] font-bold uppercase tracking-widest text-slate block mb-1">
          Note <span className="text-slate font-normal normal-case">(optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note..."
          rows={2}
          className="w-full border border-stone rounded-lg px-3 py-3 text-body text-midnight
                     focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum resize-none"
        />
      </div>

      {error && <p className="text-[12px] text-red-500 font-medium">{error}</p>}

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 btn-base bg-white text-midnight border border-stone hover:bg-mist min-h-[44px]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={logging}
          className="flex-1 btn-base bg-plum text-white hover:bg-plum/90 min-h-[44px] disabled:opacity-50"
        >
          {logging ? 'Saving…' : 'Log Payment'}
        </button>
      </div>
    </div>
  );
}

// ── Request Payment Form ─────────────────────────────────────────────────────

interface RequestPaymentFormProps {
  sending: boolean;
  hasPhone: boolean;
  hasEmail: boolean;
  onSubmit: (channels: ('sms' | 'email')[]) => void;
  onCancel: () => void;
}

function RequestPaymentForm({ sending, hasPhone, hasEmail, onSubmit, onCancel }: RequestPaymentFormProps) {
  const [sms, setSms] = useState(hasPhone);
  const [email, setEmail] = useState(hasEmail);
  const [error, setError] = useState('');

  const handleSubmit = () => {
    const channels: ('sms' | 'email')[] = [];
    if (sms) channels.push('sms');
    if (email) channels.push('email');
    if (channels.length === 0) {
      setError('Select at least one channel');
      return;
    }
    setError('');
    onSubmit(channels);
  };

  return (
    <div className="p-4 space-y-4">
      <p className="text-small text-slate">
        Send a payment link to the customer. They'll be able to pay via any of your configured payment methods.
      </p>

      <div>
        <label className="text-[11px] font-bold uppercase tracking-widest text-slate block mb-2">
          Send via
        </label>
        <div className="space-y-2">
          <label className={`flex items-center gap-3 px-3 py-3 rounded-lg border transition-colors min-h-[44px]
            ${!hasPhone ? 'border-stone/50 bg-stone/20 opacity-50 cursor-not-allowed' : sms ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
            <input
              type="checkbox"
              checked={sms}
              onChange={(e) => setSms(e.target.checked)}
              disabled={!hasPhone}
              className="w-5 h-5 rounded border-stone text-plum focus:ring-plum/30"
            />
            <div>
              <span className="text-small font-medium text-midnight block">SMS</span>
              {!hasPhone && <span className="text-[11px] text-slate">No phone number on file</span>}
            </div>
          </label>
          <label className={`flex items-center gap-3 px-3 py-3 rounded-lg border transition-colors min-h-[44px]
            ${!hasEmail ? 'border-stone/50 bg-stone/20 opacity-50 cursor-not-allowed' : email ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
            <input
              type="checkbox"
              checked={email}
              onChange={(e) => setEmail(e.target.checked)}
              disabled={!hasEmail}
              className="w-5 h-5 rounded border-stone text-plum focus:ring-plum/30"
            />
            <div>
              <span className="text-small font-medium text-midnight block">Email</span>
              {!hasEmail && <span className="text-[11px] text-slate">No email on file</span>}
            </div>
          </label>
        </div>
      </div>

      {error && <p className="text-[12px] text-red-500 font-medium">{error}</p>}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 btn-base bg-white text-midnight border border-stone hover:bg-mist min-h-[44px]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={sending}
          className="flex-1 btn-base bg-plum text-white hover:bg-plum/90 min-h-[44px] disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Send Request'}
        </button>
      </div>
    </div>
  );
}
